/*
Copyright (C) 2009 - 2019 Broadleaf Commerce.

Licensed under the Broadleaf End User License Agreement (EULA),
Version 1.1 (the “Commercial License” located at
http://license.broadleafcommerce.org/commercial_license-1.1.txt).

Alternatively, the Commercial License may be replaced with a mutually
agreed upon license (the “Custom License”) between you and
Broadleaf Commerce. You may not use this file except in compliance
with the applicable license.
*/
import React, { useEffect, useMemo, useState } from 'react';
import { unstable } from '@broadleaf/admin-components';
import classNames from 'classnames';
import cx from 'classnames';
import { debounce, get } from 'lodash';
import { PhotoshopPicker } from 'react-color';

const { FieldDecorations, ClickOutside } = unstable;

export const ColorPickerClassSwatchLeft = 'blc-color-picker__swatch--left';
export const ColorPickerClassSwatchRight = 'blc-color-picker__swatch--right';

const AuroraColorPickerField = props => {
  const { decorated, defaultValue, metadata, formik } = props;
  const { attributes, label, name, placeholder } = metadata;
  const { errors, touched: formikTouched, setFieldValue, values } = formik;
  const [open, setOpen] = useState(false);
  const swatchAlignment = get(attributes, 'alignSwatch', 'left');
  const error = get(errors, name);
  const touched = get(formikTouched, name, false);
  const [value, setValue] = useState(
    get(values, name, '#000000') || defaultValue
  );
  const debouncedSetValue = useMemo(() => debounce(setValue, 10), [setValue]);

  useEffect(() => {
    return () => {
      if (debouncedSetValue && typeof debouncedSetValue.cancel === 'function') {
        debouncedSetValue.cancel();
      }
    };
  }, [debouncedSetValue]);

  useEffect(() => {
    setValue(get(values, name, '#000000') || defaultValue);
  }, [defaultValue, values, name]);

  return (
    <FieldDecorations fullWidth formik={formik} metadata={metadata}>
      <ClickOutside
        render={({ containerRef }) => (
          <div
            className={cx('blc-color-picker', {
              [ColorPickerClassSwatchLeft]: swatchAlignment === 'left',
              [ColorPickerClassSwatchRight]: swatchAlignment === 'right'
            })}
            ref={containerRef}
          >
            <input
              autoComplete="off"
              className={classNames(
                {
                  'form-control': true,
                  'is-invalid': touched && !!error
                },
                'blc-color-picker__textInput'
              )}
              disabled={props.disabled || props.readOnly}
              name={name}
              onBlur={formik.handleBlur}
              onFocus={() => {
                setOpen(true);
              }}
              placeholder={!decorated && !placeholder ? label : placeholder}
              value={value}
              readOnly
            />
            <span
              className={'blc-color-picker__preview'}
              onClick={() => {
                setOpen(!open);
              }}
              style={{ backgroundColor: value }}
            />
            {open && (
              <PhotoshopPicker
                className="blc-color-picker__component"
                color={value}
                header={`Pick ${label}`}
                onChange={({ hex }) => {
                  debouncedSetValue(hex.toUpperCase());
                }}
                onAccept={() => {
                  setOpen(false);
                  setFieldValue(name, value.toUpperCase());
                }}
                onCancel={() => {
                  setOpen(false);
                  setValue(get(values, name, '#000000'));
                }}
              />
            )}
          </div>
        )}
        onClickOutside={() => {
          setOpen(false);
        }}
      />
    </FieldDecorations>
  );
};

export default AuroraColorPickerField;
export { AuroraColorPickerField };
